import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { Loader2 } from 'lucide-react'

import { cn } from '@/lib/cn'

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'inverted'
type Size = 'sm' | 'md' | 'lg' | 'icon'

/**
 * Bentuk tombol Threads: persegi bersudut 10 piksel, huruf biasa setengah
 * tebal, dan tombol utama berwarna kebalikan halaman — hitam di tema terang,
 * putih di tema gelap. Tidak ada warna merek pada tombol.
 */
const variants: Record<Variant, string> = {
  primary: 'bg-inverted text-on-inverted hover:opacity-85 active:opacity-75',
  inverted: 'bg-inverted text-on-inverted hover:opacity-85 active:opacity-75',
  secondary: 'bg-sunken text-ink hover:bg-line',
  outline: 'border border-line bg-transparent text-ink hover:bg-sunken',
  ghost: 'text-muted hover:bg-sunken hover:text-ink',
  danger: 'bg-danger text-white hover:opacity-90',
  success: 'bg-success text-white hover:opacity-90',
}

const sizes: Record<Size, string> = {
  sm: 'h-9 px-4 text-[15px] gap-1.5',
  md: 'h-10 px-5 text-[15px] gap-2',
  lg: 'h-12 px-6 text-[15px] gap-2.5',
  icon: 'h-10 w-10 justify-center',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  fullWidth?: boolean
}

/**
 * Tombol dasar aplikasi.
 *
 * Keadaan memuat menonaktifkan tombol sekaligus menampilkan pemutar, karena
 * tombol yang tampak aktif padahal sedang mengirim permintaan adalah undangan
 * untuk menekannya dua kali — pada operasi uang, itu masalah nyata.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'primary', size = 'md', loading = false, fullWidth, disabled, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center rounded-[10px] font-semibold transition',
        'disabled:cursor-not-allowed disabled:opacity-40',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        variants[variant],
        sizes[size],
        fullWidth && 'w-full justify-center',
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
      {children}
    </button>
  )
})
